import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Send, Sparkles, Loader2, X, Bot, User, Package, RotateCcw } from "lucide-react";
import { useMutation } from "@apollo/client";
import { CHAT_CAMPAIGN_ASSISTANT } from "@/Graphql/Queries";
import logger from '@/utils/logger';

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface BillboardPackage {
  id: string;
  displayName: string;
  name: string;
  type: string;
  size: string;
  cost: number;
  currencyCode: string;
  thumbnailPath: string;
  formattedAddress: string;
  spotsPerDay: number;
}

interface ExtractedData {
  budget?: number;
  startDate?: string;
  endDate?: string;
  country?: string;
  state?: string;
  district?: string;
  location?: string;
  businessType?: string;
  purpose?: string;
  venueType?: string;
}

interface DynamicCampaignProps {
  onClose?: () => void;
  isMobile?: boolean;
  showHeader?: boolean;
}

export default function DynamicCampaign({
  onClose,
  isMobile = false,
  showHeader = true,
}: DynamicCampaignProps = {}) {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string>(
    `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  );
  const [extractedData, setExtractedData] = useState<ExtractedData>({});
  const [billboardPackage, setBillboardPackage] = useState<BillboardPackage[] | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [rawBillboardData, setRawBillboardData] = useState<any>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // GraphQL mutation for chat
  const [chatMutation] = useMutation(CHAT_CAMPAIGN_ASSISTANT);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const { data } = await chatMutation({
        variables: {
          sessionId: sessionId,
          message: userMessage,
        },
      });

      if (data?.chatCampaignAssistant) {
        const response = data.chatCampaignAssistant;
        
        // Update extracted data from GraphQL response
        if (response.extractedDetails) {
          const details = response.extractedDetails;
          // Flatten nested place object
          const flattenedData: ExtractedData = {
            ...details,
            district: details.place?.district,
            state: details.place?.state,
            country: details.place?.country,
            venueType: details.venueType,
          };
          // Remove the nested place object
          delete (flattenedData as any).place;
          setExtractedData(flattenedData);
        }
        
        // Update billboard package if available - FIX: Use .data array instead of entire object
        if (response.billboardPackage?.data) {
          setBillboardPackage(response.billboardPackage.data);
          setRawBillboardData(response.billboardPackage);
        }
        
        // Update completion status
        setIsComplete(response.isComplete || false);
        
        // Add AI response to messages
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: response.aiMessage },
        ]);
      }
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Connection error. Please try again.",
        },
      ]);
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleViewPackage = () => {
    if (!billboardPackage || !extractedData || !rawBillboardData) return;
    
    try {
      // Transform billboard data to PackageData format with enhanced data
      const screens = billboardPackage.map((billboard) => ({
        id: billboard.id, // Keep original string ID
        inventoryId: parseInt(billboard.id),
        displayName: billboard.displayName || billboard.name,
        assetUrl: billboard.thumbnailPath,
        location: billboard.formattedAddress || `${extractedData.district || ''}, ${extractedData.state || ''}, ${extractedData.country || ''}`.replace(/, ,/g, ',').replace(/^, |, $/g, ''),
        screenType: billboard.type,
        size: billboard.size,
        price: billboard.cost,
        impressions: billboard.spotsPerDay ? billboard.spotsPerDay * 1000 : 0,
        spots: billboard.spotsPerDay || 0,
        days: extractedData.startDate && extractedData.endDate ? 
          Math.ceil((new Date(extractedData.endDate).getTime() - new Date(extractedData.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1 : 0,
        hours: 12,
      }));
    
    const currencyCode = billboardPackage[0]?.currencyCode || 'USD';
    
    // Calculate duration in days (including start date)
    const startDate = extractedData.startDate ? new Date(extractedData.startDate) : new Date();
    const endDate = extractedData.endDate ? new Date(extractedData.endDate) : new Date();
    logger.log('Date Calculation Debug:', {
      rawStartDate: extractedData.startDate,
      rawEndDate: extractedData.endDate,
      parsedStartDate: startDate,
      parsedEndDate: endDate,
      timeDiff: endDate.getTime() - startDate.getTime(),
      daysDiff: (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    });
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    
    // Calculate total budget: sum of daily prices × total days
    const totalDailyPrice = billboardPackage.reduce((sum, b) => sum + b.cost, 0);
    const totalBudget = totalDailyPrice * totalDays;
    
    // Calculate impressions: (daily impressions per month / 30) × total days
    const dailyImpressionsAll = screens.reduce((sum, s) => sum + (s.impressions || 0), 0);
    const dailyImpressions = dailyImpressionsAll / 30;
    const totalImpressions = Math.round(dailyImpressions * totalDays);
    
    const packageData = {
      packageId: `ai_${sessionId}`,
      sessionId: sessionId,
      screens: screens,
      totalScreens: billboardPackage.length,
      budget: totalBudget,
      totalImpressions: totalImpressions,
      totalReach: Math.floor(totalImpressions * 0.7),
      totalSpots: 0,
      avgFrequency: 0,
      location: `${extractedData.district || ''}, ${extractedData.state || ''}`.replace(/, $/g, ''),
      country: extractedData.country || '',
      startDate: extractedData.startDate || '',
      endDate: extractedData.endDate || '',
      duration: `${totalDays} days`,
      totalDays: totalDays,
      businessType: extractedData.purpose,
      venueType: extractedData.venueType,
      status: 'pending',
      currency: {
        code: currencyCode,
        symbol: getCurrencySymbol(currencyCode),
        name: currencyCode,
      },
      hoursPerDay: 12,
    };
    
    // Store enhanced data in sessionStorage
    sessionStorage.setItem("pendingPackage", JSON.stringify(packageData));
    sessionStorage.setItem("aiCampaignData", JSON.stringify(extractedData));
    sessionStorage.setItem("aiSessionId", sessionId || "");
    sessionStorage.setItem("aiBillboardData", JSON.stringify(rawBillboardData));
    
    // Navigate to review page
    navigate(`/ai-package-review/${packageData.packageId}`);
    } catch (error) {
      console.error('Error in handleViewPackage:', error);
    }
  };
  
  const getCurrencySymbol = (code: string): string => {
    const symbols: { [key: string]: string } = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'INR': '₹', 'JPY': '¥',
      'AUD': 'A$', 'CAD': 'C$', 'SGD': 'S$', 'MYR': 'RM', 'THB': '฿',
    };
    return symbols[code] || code;
  };

  const handleReset = () => {
    setMessages([]);
    setSessionId(`user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    setExtractedData({});
    setBillboardPackage(null);
    setRawBillboardData(null);
    setIsComplete(false);
    setInputValue("");
  };

  const formatFieldLabel = (key: string): string => {
    const labels: { [key: string]: string } = {
      budget: "Budget",
      startDate: "Start",
      endDate: "End",
      country: "Country",
      state: "State",
      district: "District",
      location: "Location",
      businessType: "Business",
      purpose: "Purpose",
      venueType: "Venue Type",
    };
    return labels[key] || key;
  };

  const formatFieldValue = (key: string, value: any): string => {
    if (key === "budget" && typeof value === "number") {
      return value.toLocaleString();
    }
    return String(value);
  };

  return (
    <div className="w-full h-full flex flex-col my-8">
      <Card className="bg-gray-900/95 backdrop-blur-md border border-gray-700/50 shadow-2xl rounded-2xl flex-1 flex flex-col h-full">
        <div className="p-3 flex flex-col h-full overflow-hidden max-h-full">
          {showHeader && (
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-700/50">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-white font-semibold text-sm">Moving Walls AI</h3>
            </div>
              <div className="flex items-center gap-2">
                {messages.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleReset}
                    className="text-gray-400 hover:text-white hover:bg-gray-700/50"
                    aria-label="Reset chat"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                )}
                {onClose && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    className="text-gray-400 hover:text-white hover:bg-gray-700/50"
                    aria-label="Close chat"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          )}

          <div
            ref={chatContainerRef}
            className="overflow-y-auto mb-3 space-y-3 custom-scrollbar flex-1 max-h-full"
            style={{ minHeight: '150px' }}
          >
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-6">
                <Bot className="w-12 h-12 text-emerald-500/50 mb-3" />
                <p className="text-gray-300 text-sm mb-4">Tell me about your advertising needs and I'll help you find the perfect billboards.</p>
                <div className="flex flex-wrap justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setInputValue("I need billboards in Singapore for my restaurant for 2 weeks with 3000 budget");
                      inputRef.current?.focus();
                    }}
                    className="text-xs text-gray-400 hover:text-emerald-400 bg-gray-800/70 hover:bg-gray-700/70 px-3 py-1.5 rounded-full border border-gray-600/50 transition-colors"
                  >
                    Restaurant in Singapore
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setInputValue("10000 budget for retail advertising in Kuala Lumpur next month");
                      inputRef.current?.focus();
                    }}
                    className="text-xs text-gray-400 hover:text-emerald-400 bg-gray-800/70 hover:bg-gray-700/70 px-3 py-1.5 rounded-full border border-gray-600/50 transition-colors"
                  >
                    Retail in Malaysia
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setInputValue("Plan a mall advertising campaign in Chennai, Tamil Nadu, India to promote a coffee shop with an investment of 10,000, running for 1 month");
                      inputRef.current?.focus();
                    }}
                    className="text-xs text-gray-400 hover:text-emerald-400 bg-gray-800/70 hover:bg-gray-700/70 px-3 py-1.5 rounded-full border border-gray-600/50 transition-colors"
                  >
                    Cafe in India
                  </button>
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex gap-2 ${
                      msg.role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    {msg.role === "assistant" && (
                      <div className="w-6 h-6 rounded-full bg-emerald-600/20 flex items-center justify-center flex-shrink-0 mt-1">
                        <Bot className="w-3.5 h-3.5 text-emerald-400" />
                      </div>
                    )}
                    <div
                      className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                        msg.role === "user"
                          ? "bg-emerald-600 text-white"
                          : "bg-gray-700/70 text-gray-100"
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    {msg.role === "user" && (
                      <div className="w-6 h-6 rounded-full bg-gray-600/50 flex items-center justify-center flex-shrink-0 mt-1">
                        <User className="w-3.5 h-3.5 text-gray-300" />
                      </div>
                    )}
                  </div>
                ))}
                {isLoading && (
                  <div className="flex gap-2 justify-start">
                    <div className="w-6 h-6 rounded-full bg-emerald-600/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <Bot className="w-3.5 h-3.5 text-emerald-400" />
                    </div>
                    <div className="bg-gray-700/70 rounded-xl px-4 py-2">
                      <div className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                        <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                        <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {Object.keys(extractedData).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {Object.entries(extractedData).map(
                ([key, value]) =>
                  value && key !== '__typename' && (
                    <Badge
                      key={key}
                      variant="secondary"
                      className="bg-emerald-600/20 text-emerald-300 border-emerald-500/30 text-xs"
                    >
                      {formatFieldLabel(key)}: {formatFieldValue(key, value)}
                    </Badge>
                  )
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex gap-2 items-center mb-2">
            <div className="relative flex-1 h-11">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={
                  messages.length === 0
                    ? "Describe your campaign..."
                    : "Continue your conversation..."
                }
                className="w-full h-full px-4 py-3 rounded-xl bg-gray-800/80 text-white border border-gray-600/50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 text-sm"
                disabled={isLoading}
              />
              {messages.length === 0 && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Sparkles className="w-4 h-4 text-gray-500" />
                </div>
              )}
            </div>
            <Button
              type="submit"
              disabled={isLoading || !inputValue.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-4 h-11"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>

          {isComplete && billboardPackage && billboardPackage.length > 0 && (
            <Button
              onClick={handleViewPackage}
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl py-2 font-semibold shadow-lg text-sm"
              disabled={isLoading}
            >
              <Package className="h-4 w-4 mr-2" />
              View Dynamic Package ({billboardPackage.length} Billboards)
            </Button>
          )}

        </div>
      </Card>
    </div>
  );
}
